import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Grid, Typography } from "@mui/material";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import LoadingButton from "../../../../components/Buttons/LoadingButton";
import Modal from "../../../../components/Modal";
import { useTranslation } from "react-i18next";
import { trainingLocationTypes } from "../../../../constants";
import { find } from "lodash";
import ModalTitleDropdown from "../../../../components/Modal/ModalTitleDropdown";
import ComboBox from "../../../../components/FormikFields/ComboBox";
import {
  phoneNumberValidation,
  validateZipCode
} from "../../../../utils/validation";
import TextField from "../../../../components/FormikFields/TextField";
import ConfirmLocationModal from "./ConfirmLocationModal";
import PhoneNumberInput from "../../../../components/FormikFields/PhoneNumberInput";
import useScopedTranslation from "../../../../hooks/useScopedTranslation";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import StateSelector from "../../../../components/FormikFields/StateSelector";

const defaultLocationValue = {
  name: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  latitude: "",
  longitude: ""
};

const initialValues = {
  ...defaultLocationValue,
  locationId: "",
  seats: ""
};

const getTrainingLocation = (locationList, locationId) => {
  const location = find(locationList, { id: locationId });
  return location ? location : defaultLocationValue;
};

const getSchema = (t, currentTrainingLocationType) => {
  // Determine if field is required based on if training location type is internal or external
  const fieldRequired = (
    field,
    trainingLocationType = trainingLocationTypes.external
  ) =>
    Yup.string().test(
      "trainingLocationType",
      t("validationMessages.fieldRequired"),
      function() {
        return (
          !!this.parent[field] ||
          trainingLocationType !== currentTrainingLocationType
        );
      }
    );

  const setLimit = (field, fieldName, limit) =>
    field.max(
      limit,
      t("validationMessages.cannotExceedLength", {
        type: t(`manageCourses.${fieldName}`),
        maxLength: limit
      })
    );

  return Yup.object().shape({
    locationId: fieldRequired("locationId", trainingLocationTypes.internal),
    name: setLimit(fieldRequired("name"), "locationName", 250),
    address1: setLimit(fieldRequired("address1"), "address1", 100),
    address2: setLimit(Yup.string().nullable(true), "address2", 100),
    city: setLimit(fieldRequired("city"), "city", 40),
    state: fieldRequired("state").nullable(),
    zip: fieldRequired("zip").test(
      "validZipCode",
      t("validationMessages.validZipCode"),
      function() {
        return validateZipCode(this.parent.zip);
      }
    ),
    phone: phoneNumberValidation(t),
    seats: Yup.number()
      .required(t("validationMessages.fieldRequired"))
      .min(1, t("validationMessages.greaterThanZero"))
      .max(9999, t("validationMessages.lessThanLimit", { limit: "9,999" }))
  });
};

const InternalLocationField = ({ menuItems, isLoading, placeholder }) => {
  const { t } = useTranslation();
  const hasNoOptions = !isLoading && (!menuItems || menuItems.length === 0);
  const field = (
    <ComboBox
      options={menuItems}
      name="locationId"
      placeholder={placeholder}
      InputProps={{
        inputProps: {
          "data-qa": "add-new-training-location-id-field"
        }
      }}
      isLoadingOptions={isLoading}
      disabled={hasNoOptions || isLoading}
    />
  );
  return hasNoOptions ? (
    <Tooltip title={t("manageCourses.noLocationsTooltip")}>
      <div>{field}</div>
    </Tooltip>
  ) : (
    field
  );
};
const TrainingLocationForm = ({
  locations,
  locationMenuItems,
  trainingLocationType,
  shouldReset,
  isLoadingLocations,
  isCreatingLocation
}) => {
  const {
    values,
    setFieldValue,
    resetForm,
    setSubmitting
  } = useFormikContext();

  const t = useScopedTranslation("manageCourses");

  const isInternal = trainingLocationType === trainingLocationTypes.internal;

  const setLocationValues = () => {
    // Set values for internal location
    const trainingLocation = getTrainingLocation(locations, values.locationId);
    Object.keys(defaultLocationValue).forEach(key => {
      setFieldValue(key, trainingLocation[key]);
    });
  };

  useEffect(() => {
    if (values.locationId) {
      setLocationValues();
    } else {
      resetForm();
    }
  }, [values.locationId]);

  useEffect(() => {
    resetForm();
  }, [trainingLocationType, shouldReset]);

  useEffect(() => {
    setSubmitting(isCreatingLocation);
  }, [isCreatingLocation]);

  return (
    <Form>
      <Grid container spacing={4} sx={{ mt: -0.5 }}>
        {isInternal && (
          <Grid item xs={12}>
            <InternalLocationField
              placeholder={t("location")}
              menuItems={locationMenuItems}
              isLoading={isLoadingLocations}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("locationName")}
            name={"name"}
            disabled={isInternal}
            tooltip={isInternal && t("internalLocationTooltip")}
            inputProps={{
              "data-qa": "add-new-training-location-name-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("address1")}
            name={"address1"}
            disabled={isInternal}
            tooltip={isInternal && t("internalLocationTooltip")}
            inputProps={{
              "data-qa": "add-new-training-location-address1-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("address2")}
            name="address2"
            disabled={isInternal}
            tooltip={isInternal && t("internalLocationTooltip")}
            inputProps={{
              "data-qa": "add-new-training-location-address2-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("city")}
            name="city"
            disabled={isInternal}
            tooltip={isInternal && t("internalLocationTooltip")}
            inputProps={{
              "data-qa": "add-new-training-location-city-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StateSelector
            disabled={isInternal}
            disableUnderline={false}
            tooltip={isInternal && t("internalLocationTooltip")}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t("zip")}
            name="zip"
            disabled={isInternal}
            tooltip={isInternal && t("internalLocationTooltip")}
            inputProps={{
              "data-qa": "add-new-training-location-zip-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <PhoneNumberInput
            name="phone"
            label={t("phone")}
            inputProps={{
              "data-qa": "add-new-training-location-phone-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            type="number"
            label={t("seats")}
            name="seats"
            inputProps={{
              "data-qa": "add-new-training-location-seats-field"
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: 3 }}>
          {isInternal && (
            <Typography
              variant="caption"
              color="textSecondary"
              data-qa="update-location-warning"
            >
              {`*${t("updateLocationWarning")}`}.
            </Typography>
          )}
        </Grid>
      </Grid>
    </Form>
  );
};

const AddNewTrainingLocationModal = ({
  defaultTrainingLocationType,
  onClose,
  open,
  onCreateTrainingLocation,
  locations,
  isCreating,
  isLoadingLocations
}) => {
  const { t } = useTranslation();

  const [trainingLocationType, setTrainingLocationType] = useState(
    trainingLocationTypes.internal
  );
  const [confirmAddressModalOpen, setConfirmAddressModalOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmAddressModalOpen(false);
      setTrainingLocationType(trainingLocationTypes.internal);
    }
  }, [open]);

  useEffect(() => {
    setTrainingLocationType(defaultTrainingLocationType);
  }, [defaultTrainingLocationType]);

  const locationMenuItems = useMemo(() => {
    return (
      locations &&
      locations.map(location => ({
        value: location.id,
        label: location.name
      }))
    );
  }, [locations]);

  const trainingLocationMenuItems = Object.values(trainingLocationTypes).map(
    trainingLocationType => ({
      value: trainingLocationType,
      label: t("manageCourses.trainingLocationType", {
        context: trainingLocationType
      })
    })
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={getSchema(t, trainingLocationType)}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          if (trainingLocationType === trainingLocationTypes.external) {
            setConfirmAddressModalOpen(true);
            setSubmitting(false);
          } else {
            onCreateTrainingLocation(values);
          }
        }}
      >
        {({ submitForm, values, resetForm }) => (
          <>
            <Modal
              scrollable
              maxWidth="md"
              fullWidth
              fillHeight
              dialogTitle={
                <ModalTitleDropdown
                  label={`${t("manageCourses.addNewLabel")}:`}
                  value={trainingLocationType}
                  options={trainingLocationMenuItems}
                  onChange={setTrainingLocationType}
                  selectWidth={342}
                />
              }
              dialogContent={
                <TrainingLocationForm
                  locations={locations}
                  trainingLocationType={trainingLocationType}
                  shouldReset={open}
                  isLoadingLocations={isLoadingLocations}
                  locationMenuItems={locationMenuItems}
                />
              }
              dialogActions={
                <>
                  <Button
                    color="primary"
                    onClick={() => {
                      onClose();
                      resetForm();
                    }}
                  >
                    {t("cancelBtn")}
                  </Button>
                  <LoadingButton
                    pending={isCreating}
                    color="primary"
                    variant="contained"
                    onClick={submitForm}
                    disabled={isCreating}
                    data-qa="save-new-training-location-btn"
                  >
                    {t("saveBtn")}
                  </LoadingButton>
                </>
              }
              open={open}
              onClose={onClose}
              showCloseIcon
              data-qa="add-new-training-location-modal"
            />
            <ConfirmLocationModal
              open={confirmAddressModalOpen}
              onConfirm={coordinates =>
                onCreateTrainingLocation({ ...values, ...coordinates })
              }
              onClose={() => setConfirmAddressModalOpen(false)}
              location={values}
            />
          </>
        )}
      </Formik>
    </>
  );
};

AddNewTrainingLocationModal.propTypes = {
  /**
   * Called to close the modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to create a new training location
   */
  onCreateTrainingLocation: PropTypes.func,
  isCreating: PropTypes.bool,
  defaultTrainingLocationType: PropTypes.oneOf(
    Object.values(trainingLocationTypes)
  )
};

export default AddNewTrainingLocationModal;
